/**
 * Appwrite-Anbindung.
 *
 * Der Browser darf **nur lesen**. Jede Änderung an Zeiten läuft über eine
 * Appwrite-Funktion, die serverseitig stempelt. Deshalb gibt es hier keine
 * `createRow`/`updateRow`-Aufrufe — wer eine vermisst, sucht an der falschen
 * Stelle: die richtige liegt in `functions/`.
 */

import { Account, Client, ExecutionMethod, Functions, Query, TablesDB, Teams } from 'appwrite';
import type { Models } from 'appwrite';
import {
  parseDayEntry, parseEmployee,
  type CorrectionRequest, type DayEntry, type DayKey, type Employee, type StampAction,
} from '@zeit/domain';

const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
const project = import.meta.env.VITE_APPWRITE_PROJECT;

if (!endpoint || !project) {
  throw new Error(
    'VITE_APPWRITE_ENDPOINT und VITE_APPWRITE_PROJECT fehlen — .env aus .env.example anlegen.',
  );
}

export const client = new Client().setEndpoint(endpoint).setProject(project);

export const account = new Account(client);
export const tablesDB = new TablesDB(client);
export const functions = new Functions(client);
export const teams = new Teams(client);

export const DB = 'main';
export const TABLE = {
  employees: 'employees',
  dayEntries: 'day_entries',
  corrections: 'correction_requests',
  auditLog: 'audit_log',
} as const;

/** Fehler mit einer Meldung, die der Oberfläche direkt gezeigt werden darf. */
export class ApiError extends Error {
  readonly code: string;

  constructor(message: string, code = 'unknown') {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

/**
 * Ruft eine Appwrite-Funktion synchron auf und packt die Antwort aus.
 *
 * Appwrite meldet auch fachliche Fehler als erfolgreiche Ausführung mit einem
 * Fehlerrumpf — deshalb wird hier zweistufig geprüft: erst ob die Ausführung
 * überhaupt lief, dann ob sie fachlich geklappt hat.
 */
async function call<T>(functionId: string, payload: Record<string, unknown>): Promise<T> {
  let execution: Models.Execution;
  try {
    execution = await functions.createExecution({
      functionId,
      body: JSON.stringify(payload),
      async: false,
      method: ExecutionMethod.POST,
      headers: { 'content-type': 'application/json' },
    });
  } catch (err) {
    throw new ApiError(
      err instanceof Error ? err.message : 'Die Anfrage konnte nicht gesendet werden.',
      'transport',
    );
  }

  if (execution.status !== 'completed') {
    throw new ApiError('Die Verarbeitung auf dem Server ist fehlgeschlagen.', 'execution');
  }

  let body: { ok?: boolean; error?: string; code?: string } & Record<string, unknown>;
  try {
    body = JSON.parse(execution.responseBody);
  } catch {
    throw new ApiError('Unerwartete Antwort vom Server.', 'parse');
  }

  if (!body.ok) throw new ApiError(body.error ?? 'Unbekannter Fehler.', body.code ?? 'unknown');
  return body as T;
}

// --- Konto ---

export const auth = {
  current: () => account.get(),

  async login(email: string, password: string) {
    return account.createEmailPasswordSession({ email, password });
  },

  logout: () => account.deleteSession({ sessionId: 'current' }),

  /** Stösst die Passwort-Zurücksetzen-Mail an. */
  recover: (email: string, url: string) => account.createRecovery({ email, url }),

  completeRecovery: (userId: string, secret: string, password: string) =>
    account.updateRecovery({ userId, secret, password }),

  /**
   * Passwort über den Einladungslink festlegen. Läuft ohne Sitzung — die
   * Funktion `invite-accept` prüft stattdessen den Schlüssel aus dem Link.
   */
  acceptInvite: (userId: string, token: string, password: string) =>
    call<{ email: string }>('invite-accept', { userId, token, password }),

  /**
   * Gehört das angemeldete Konto zur Verwaltung?
   *
   * `teams.list()` liefert nur die eigenen Mitgliedschaften — die Antwort ist
   * also vertrauenswürdig für die *Anzeige*. Die eigentliche Absicherung
   * liegt trotzdem in den Funktionen: wer hier trickst, sieht höchstens einen
   * Menüpunkt, hinter dem jeder Aufruf abgewiesen wird.
   */
  async isAdmin(): Promise<boolean> {
    try {
      const res = await teams.list();
      return res.teams.some((t) => t.$id === 'admins');
    } catch {
      return false;
    }
  },
};

/**
 * Holt alle Seiten einer Abfrage.
 *
 * Appwrite liefert höchstens 100 Zeilen je Anfrage. Ohne Blättern schnitt die
 * Abfrage ab, sobald jemand mehr als hundert erfasste Tage hatte — und zwar
 * lautlos: der Saldo rechnete dann mit den ältesten hundert Tagen, und der
 * Export der laufenden Woche kam leer heraus, weil ihre Tage gar nicht mehr
 * in der Antwort standen. Die Serverfunktion blättert längst, der Browser
 * jetzt auch.
 *
 * Die Abfrage muss eine Sortierung mitbringen, sonst ist der Zeiger beliebig.
 */
async function listAll(tableId: string, queries: string[]): Promise<Models.DefaultRow[]> {
  const PAGE = 100;
  const out: Models.DefaultRow[] = [];
  let cursor: string | null = null;

  for (let page = 0; page < 100; page++) {
    const q = [...queries, Query.limit(PAGE)];
    if (cursor) q.push(Query.cursorAfter(cursor));
    const res = await tablesDB.listRows({ databaseId: DB, tableId, queries: q });
    out.push(...res.rows);
    if (res.rows.length < PAGE) break;
    cursor = res.rows[res.rows.length - 1].$id;
  }

  return out;
}

// --- Lesen ---

export const read = {
  /** Das Profil zum angemeldeten Konto. `null`, wenn keines hinterlegt ist. */
  async myEmployee(userId: string): Promise<Employee | null> {
    const res = await tablesDB.listRows({
      databaseId: DB,
      tableId: TABLE.employees,
      queries: [Query.equal('userId', userId), Query.limit(1)],
    });
    return res.rows.length ? parseEmployee(res.rows[0]) : null;
  },

  /**
   * Tage eines Zeitraums, einschliesslich beider Grenzen.
   *
   * Die Zeilenrechte filtern bereits auf das eigene Profil; `employeeId`
   * steht trotzdem in der Abfrage, damit der zusammengesetzte Index greift.
   */
  async days(employeeId: string, from: DayKey, to: DayKey): Promise<DayEntry[]> {
    const rows = await listAll(TABLE.dayEntries, [
      Query.equal('employeeId', employeeId),
      Query.greaterThanEqual('date', from),
      Query.lessThanEqual('date', to),
      Query.orderAsc('date'),
    ]);
    return rows.map(parseDayEntry);
  },

  /** Der letzte Tag mit Feierabend vor `before` — für die Ruhezeit nach § 5. */
  async previousClockOut(employeeId: string, before: DayKey): Promise<number | null> {
    const res = await tablesDB.listRows({
      databaseId: DB,
      tableId: TABLE.dayEntries,
      queries: [
        Query.equal('employeeId', employeeId),
        Query.lessThan('date', before),
        Query.isNotNull('clockOut'),
        Query.orderDesc('date'),
        Query.limit(1),
      ],
    });
    const row = res.rows[0];
    return row?.clockOut ? new Date(row.clockOut as string).getTime() : null;
  },

  /** Eigene Korrekturanträge eines Zeitraums. */
  async corrections(employeeId: string, from: DayKey, to: DayKey): Promise<CorrectionRequest[]> {
    const rows = await listAll(TABLE.corrections, [
      Query.equal('employeeId', employeeId),
      Query.greaterThanEqual('date', from),
      Query.lessThanEqual('date', to),
      Query.orderAsc('date'),
    ]);
    return rows.map((r) => r as unknown as CorrectionRequest);
  },
};

// --- Schreiben, ausschliesslich über Funktionen ---

export interface ReminderStatus {
  publicKey: string;
  time: string;
  pausedUntil: DayKey;
  /** Ermittelte übliche Anfangszeit, ohne feste Uhrzeit. */
  detected: string | null;
  next: { day: DayKey; time: string } | null;
  thisDevice: boolean;
  otherDevices: number;
}

export const api = {
  /** Holt die Serverzeit, um die Geräteuhr abzugleichen. */
  serverTime: () => call<{ now: number }>('stamp', { action: 'time' }),

  /** Stempeln. Der Zeitpunkt wird serverseitig gesetzt, nie hier. */
  stamp: (action: StampAction) =>
    call<{ entry: Record<string, unknown>; warnings: unknown[]; now: number }>('stamp', { action }),

  submitCorrection: (input: {
    date: DayKey;
    in: string | null;
    out: string | null;
    breakMinutes: number;
    reason: string;
  }) => call<{ request: CorrectionRequest }>('corrections', { op: 'submit', ...input }),

  decideCorrection: (input: { requestId: string; approve: boolean; note?: string }) =>
    call<{ request: CorrectionRequest }>('corrections', { op: 'decide', ...input }),

  /**
   * Morgenerinnerung. `endpoint` ist das Abo dieses Geräts, soweit vorhanden
   * — daran erkennt der Server, ob „dieses Gerät“ schon angemeldet ist.
   */
  reminders: {
    status: (endpoint?: string) => call<ReminderStatus>('reminders', { op: 'status', endpoint }),

    subscribe: (subscription: PushSubscriptionJSON, device: string) =>
      call<ReminderStatus>('reminders', { op: 'subscribe', subscription, device }),

    unsubscribe: (endpoint: string) =>
      call<ReminderStatus>('reminders', { op: 'unsubscribe', endpoint }),

    settings: (input: { time?: string; pausedUntil?: string; endpoint?: string }) =>
      call<ReminderStatus>('reminders', { op: 'settings', ...input }),

    test: (endpoint: string) => call<object>('reminders', { op: 'test', endpoint }),
  },

  admin: {
    invite: (input: {
      name: string;
      email: string;
      department: string;
      weeklyTargetHours: number;
      workdaysPerWeek: number;
      openingBalanceHours: number;
      employmentStart: DayKey;
    }) =>
      call<{ employee: Employee; mailError?: string; validDays: number }>('admin', {
        op: 'invite',
        ...input,
      }),

    updateEmployee: (input: { employeeId: string } & Partial<Employee>) =>
      call<{ employee: Employee }>('admin', { op: 'update', ...input }),

    setActive: (employeeId: string, active: boolean) =>
      call<{ employee: Employee }>('admin', { op: 'set_active', employeeId, active }),

    /** Neuer Einladungslink per Mail; der vorige wird damit ungültig. */
    sendAccess: (employeeId: string) =>
      call<{ email: string; validDays: number }>('admin', { op: 'send_access', employeeId }),

    /** Alles, was eine Verwaltungsansicht braucht — gebündelt in einem Aufruf. */
    overview: (from: DayKey, to: DayKey) =>
      call<{
        employees: Employee[];
        entries: Record<string, unknown>[];
        pending: CorrectionRequest[];
      }>('admin', { op: 'overview', from, to }),

    auditLog: (limit = 100) =>
      call<{ entries: Record<string, unknown>[] }>('admin', { op: 'audit', limit }),
  },
};
